import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate, cn } from '@/lib/utils';
import { Plus, Server, HardDrive, Cloud, Key, Zap, Globe, Calendar, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const statusColors: Record<string, string> = {
  running: 'bg-emerald-500',
  stopped: 'bg-gray-400',
  provisioning: 'bg-yellow-500',
  error: 'bg-red-500',
  starting: 'bg-blue-500',
  stopping: 'bg-orange-500',
  paused: 'bg-amber-500',
  destroying: 'bg-red-600',
};

export function Instances() {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['instances'],
    queryFn: async () => {
      const { data } = await api.get('/instances');
      return data;
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('dashboard.instances')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('instances.subtitle')}
          </p>
        </div>
        <Link to="/dashboard/new">
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            {t('dashboard.newInstance')}
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Skeleton className="h-11 w-11 rounded-xl flex-shrink-0" />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-16 rounded-full" />
                      </div>
                      <Skeleton className="h-3 w-44" />
                      <Skeleton className="h-3 w-36" />
                    </div>
                  </div>
                  <div className="flex items-center gap-6 flex-shrink-0">
                    <div className="hidden sm:block text-right">
                      <Skeleton className="h-4 w-16 mb-1" />
                      <Skeleton className="h-3 w-28" />
                    </div>
                    <Skeleton className="h-3 w-20 hidden md:block" />
                    <Skeleton className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !data?.instances?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mb-6">
              <Server className="h-10 w-10 text-muted-foreground/40" />
            </div>
            <h3 className="text-lg font-medium mb-2">{t('dashboard.noInstances')}</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md text-center">
              {t('dashboard.noInstancesDescription')}
            </p>
            <Link to="/dashboard/new">
              <Button size="lg" className="gap-2">
                <Plus className="h-4 w-4" />
                {t('dashboard.createFirst')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.instances.map((instance: any, index: number) => {
            const hostingType = instance.hostingType || instance.hosting_type;
            const keyMode = instance.keyMode || instance.key_mode;
            const hostingIcon = hostingType === 'byos' ? HardDrive : Cloud;
            const keyModeIcon = keyMode === 'own_key' ? Key : Zap;

            return (
              <motion.div
                key={instance.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/dashboard/instances/${instance.id}`}>
                  <Card className="hover:shadow-md transition-all hover:border-primary/30 cursor-pointer group">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        {/* Left: Status & Info */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Status Indicator */}
                          <div className="relative flex-shrink-0">
                            <div className={cn(
                              'h-11 w-11 rounded-xl flex items-center justify-center',
                              instance.status === 'running' ? 'bg-emerald-500/10' :
                              instance.status === 'error' ? 'bg-red-500/10' :
                              instance.status === 'provisioning' ? 'bg-yellow-500/10' :
                              'bg-gray-500/10'
                            )}>
                              <Server className={cn(
                                'h-5 w-5',
                                instance.status === 'running' ? 'text-emerald-500' :
                                instance.status === 'error' ? 'text-red-500' :
                                instance.status === 'provisioning' ? 'text-yellow-500' :
                                'text-gray-500'
                              )} />
                            </div>
                            <div className={cn(
                              'absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-background flex items-center justify-center',
                              statusColors[instance.status] || 'bg-gray-400'
                            )}>
                              {instance.status === 'running' && (
                                <div className={cn('absolute inset-0 rounded-full animate-ping opacity-40', statusColors[instance.status])} />
                              )}
                            </div>
                          </div>

                          {/* Instance Details */}
                          <div className="flex-1 min-w-0">
                            {/* Name & Status */}
                            <div className="flex items-center gap-2 mb-1.5">
                              <h3 className="font-bold truncate">{instance.name}</h3>
                              <Badge
                                variant={
                                  instance.status === 'running' ? 'success' :
                                  instance.status === 'error' ? 'destructive' :
                                  instance.status === 'provisioning' ? 'default' :
                                  'secondary'
                                }
                                className="capitalize text-xs"
                              >
                                {t(`dashboard.instance.${instance.status}` as any) || instance.status}
                              </Badge>
                            </div>

                            {/* Hosting & Key Mode */}
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1">
                              <div className="flex items-center gap-1.5">
                                {React.createElement(hostingIcon, { className: 'h-3.5 w-3.5' })}
                                <span className="font-medium">{hostingType === 'managed' ? 'Managed' : 'BYOS'}</span>
                              </div>
                              <span>•</span>
                              <div className="flex items-center gap-1.5">
                                {React.createElement(keyModeIcon, { className: 'h-3.5 w-3.5' })}
                                <span>{keyMode === 'platform_key' ? 'Platform' : 'Own Key'}</span>
                              </div>
                            </div>

                            {/* AI Model & Channels */}
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {(instance.aiModel || instance.ai_model) && (
                                <div className="flex items-center gap-1.5">
                                  <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                                  <span className="font-mono text-[10px]">
                                    {(instance.aiModel || instance.ai_model).replace('openrouter/', '').replace('openai/', '')}
                                  </span>
                                </div>
                              )}
                              {instance.channels && instance.channels.length > 0 && (
                                <>
                                  <span>•</span>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {instance.channels.map((channel: string) => (
                                      <Badge key={channel} variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                        {channel}
                                      </Badge>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right: Metadata & Cost */}
                        <div className="flex items-center gap-6 flex-shrink-0">
                          {/* Cost & URL */}
                          <div className="text-right hidden sm:block">
                            {hostingType === 'byos' ? (
                              <p className="font-bold text-sm text-emerald-600">$5</p>
                            ) : (
                              <div className="mb-1">
                                <p className="font-bold text-sm">
                                  ${(parseInt(instance.monthlyCostCents || instance.monthly_cost_cents) || 0) / 100}
                                  <span className="text-xs text-muted-foreground font-normal">/mo</span>
                                </p>
                                {keyMode === 'platform_key' && (
                                  <p className="text-[10px] text-amber-600 font-medium">+ wallet usage</p>
                                )}
                              </div>
                            )}
                            {(instance.openclawUrl || instance.openclaw_url || instance.server_ip) && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground justify-end">
                                <Globe className="h-3 w-3" />
                                <span className="truncate max-w-[150px]">
                                  {instance.openclawUrl?.replace('https://', '').replace('http://', '') ||
                                   instance.openclaw_url?.replace('https://', '').replace('http://', '') ||
                                   instance.server_ip}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Created Date */}
                          <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>{formatDate(instance.createdAt || instance.created_at)}</span>
                          </div>

                          {/* Arrow */}
                          <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
